import { ReactNode, useState } from 'react';
import { Chart, Analysis, RouterLink } from './styled';
import { SlideContent } from '../styled';
import Observer from '@researchgate/react-intersection-observer';
import { useQuery } from '@tanstack/react-query';
import { Grid, useTheme, Heading, Text, Bones } from '@simplywallst/ui-core';
import PNV from '@components/PositiveNegativeValue';
import MiniPriceChart from '@components/MiniPriceChart';
import { fetchAnalysis, getDefaultAnalysis } from './fetchAnalysis';
import Media from 'react-media';
import { useTracking } from 'react-tracking';

const Metric = ({
  isFetched = true,
  title = '',
  children,
}: {
  isFetched: boolean;
  title: string;
  children: ReactNode;
}) => (
  <Grid rowGap="4px">
    <Heading as="h3" typography="xSmall" color="whiteRGBA" opacity={0.5}>
      {title}
    </Heading>
    {isFetched ? (
      children
    ) : (
      <Bones maxWidth="60px" marginBottom="x0_25" marginTop="x0_75" />
    )}
  </Grid>
);

const AnalysisSlide = ({
  companyId = '',
  canonicalUrl = '',
}: {
  companyId?: string;
  canonicalUrl?: string;
}) => {
  const theme = useTheme();
  const [active, setActive] = useState(false);
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'AnalysisSlide',
  });
  const {
    data: analysis = getDefaultAnalysis(),
    isFetched,
    isError,
  } = useQuery({
    queryKey: [`COMPANY_ANALYSIS_SLIDE`, companyId],
    queryFn: (context) => {
      const { 1: companyId } = context.queryKey;
      return fetchAnalysis(companyId);
    },
    enabled: active && companyId.length > 0,
    staleTime: 3600000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  return (
    <SlideContent as="section" data-cy-id="analysis-slide">
      <Observer
        threshold={[0, 0.3]}
        onChange={(e) => {
          if (active === false && e.intersectionRatio > 0.3) {
            setActive(true);
          }
        }}
      >
        <Analysis rowGap="16px" templateRows="auto auto 172px 40px">
          {isError ? (
            <Text>
              We're sorry, but an error occured. Please try again later.
            </Text>
          ) : (
            <>
              <Grid templateColumns="1fr 1fr">
                <Metric title="Share Price" isFetched={isFetched && active}>
                  <Text typography="xSmallStrong">
                    {analysis.sharePriceFormatted}
                  </Text>
                </Metric>
                <Metric title="Market Cap" isFetched={isFetched && active}>
                  <Text typography="xSmallStrong">
                    {analysis.marketCapFormatted}
                  </Text>
                </Metric>
              </Grid>
              <Metric title="1Y Return" isFetched={isFetched && active}>
                <Text typography="xSmallStrong">
                  <PNV value={analysis.return1YrAbs}>
                    {analysis.return1YrAbsFormatted}
                  </PNV>
                </Text>
              </Metric>
              <Chart active={active}>
                {active && (
                  <Media query={{ maxWidth: theme.mdMaxRaw }}>
                    {(matches) => (
                      <MiniPriceChart
                        companyId={companyId}
                        height={172}
                        includeXAxis={true}
                        interactive={matches === false}
                        currencyISO={analysis.listingCurrencyISO}
                      />
                    )}
                  </Media>
                )}
              </Chart>
            </>
          )}
          {canonicalUrl.length > 0 && (
            <RouterLink
              styleType="loudLarge"
              display="block"
              data-cy-id="risks-and-rewards-cta"
              to={canonicalUrl}
              onClick={() => {
                trackEvent({
                  modifier: 'View Company Report',
                  action: 'click',
                });
              }}
            >
              View Company Report
            </RouterLink>
          )}
        </Analysis>
      </Observer>
    </SlideContent>
  );
};

export default AnalysisSlide;
